import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ENDPOINTS = {
  company: 'companies/check-duplicate',
  contact: 'contacts/check-duplicate',
  opportunity: 'opportunities/check-duplicate',
};

const ENTITY_LABELS = {
  company: { icon: '🏢', label: 'empresa', frontPath: '/companies' },
  contact: { icon: '👤', label: 'contacto', frontPath: '/contacts' },
  opportunity: { icon: '🎯', label: 'oportunidad', frontPath: '/pipeline' },
};

/**
 * Real-time duplicate warning for create dialogs.
 * @param {string} entityType - 'company' | 'contact' | 'opportunity'
 * @param {object} query - fields to send to the check-duplicate endpoint (name/email/title/company_id/typsa_org_unit_id)
 * @param {string} excludeId - optional current entity id (when editing) so it doesn't match itself
 */
const DuplicateWarning = ({ entityType, query, excludeId }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
    const primary = query.name || query.title || '';
    if (!primary || primary.trim().length < 3) {
      setMatches([]);
      return;
    }

    const controller = new AbortController();
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        Object.entries(query).forEach(([k, v]) => {
          if (v) params.set(k, v);
        });
        if (excludeId) params.set('exclude_id', excludeId);
        const res = await axios.get(`${API}/${ENDPOINTS[entityType]}?${params.toString()}`, {
          signal: controller.signal,
        });
        setMatches(res.data?.matches || []);
      } catch (err) {
        if (!axios.isCancel?.(err) && err?.name !== 'CanceledError') {
          console.error('Duplicate check failed:', err);
        }
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [entityType, JSON.stringify(query), excludeId]);

  if (dismissed || matches.length === 0) return null;

  const meta = ENTITY_LABELS[entityType];

  const _matchName = (m) => {
    if (entityType === 'contact') {
      return m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Contacto';
    }
    return m.name || m.short_title || m.title || 'Sin nombre';
  };

  return (
    <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 my-3" data-testid={`duplicate-warning-${entityType}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900">
            Puede que esta {meta.label} ya exista
          </p>
          <p className="text-xs text-amber-700 mt-0.5 mb-2">
            Revisa antes de crear un duplicado. Haz clic en el nombre para abrir la ficha existente en otra pestaña.
          </p>
          <ul className="space-y-1">
            {matches.map((m) => (
              <li key={m.id} className="flex items-center gap-2 text-xs">
                <span>{meta.icon}</span>
                <a
                  href={`${meta.frontPath}/${m.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 hover:underline font-medium truncate max-w-[280px]"
                  data-testid={`duplicate-match-${m.id}`}
                >
                  {_matchName(m)}
                </a>
                <ExternalLink className="w-3 h-3 text-blue-600 flex-shrink-0" />
                {m.match_by === 'email' && (
                  <span className="text-red-700 font-bold text-[10px] bg-red-100 px-1.5 py-0.5 rounded">EMAIL EXACTO</span>
                )}
                {m.same_client && (
                  <span className="text-amber-800 font-bold text-[10px] bg-amber-100 px-1.5 py-0.5 rounded">MISMO CLIENTE</span>
                )}
                <span className="text-gray-500 text-[10px]">score {(m.score * 100).toFixed(0)}%</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="mt-2 text-xs text-amber-700 hover:text-amber-900 underline"
            data-testid={`duplicate-dismiss-${entityType}`}
          >
            No es duplicado, continuar
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateWarning;
